import React from 'react';
import {
  Box,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import { History } from '@material-ui/icons';
import { unpackToDateTime } from '@campfire/hot-date';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { entityTypeHeading, entityTypeIcon } from './enterprise-entity-labels';
import { GET_UPDATE_HISTORY } from './enterprise-update-history.gql';
import {
  GetUpdateHistory,
  GetUpdateHistory_vm_enterpriseUpdateHistory as HistoryEntry,
} from './__generated__/GetUpdateHistory';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: 40,
      paddingRight: 40,
      paddingTop: 20,
      backgroundColor: '#F9F9F9',
      minHeight: 'calc(100% - 20px)',
    },
    heading: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: '8px',
      borderBottom: '1px solid #efefef',
    },
    headingTitle: {
      color: theme.color.grey.neutralBrand900,
      fontWeight: 600,
      paddingBottom: '5px',
      fontSize: 20,
    },
    containerPanel: {
      background: 'white',
      paddingTop: '1.25rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.875rem',
      paddingRight: '1.875rem',
      marginTop: '2.25rem',
    },
    headingPanel: {
      fontWeight: 600,
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    bodyPanel: {
      fontSize: '14px',
      color: '#808080',
    },
    tableHead: {
      '& th': {
        fontWeight: 600,
        fontSize: '11px',
        textTransform: 'uppercase' as const,
        color: theme.color.grey.neutral200,
        borderBottom: '1px solid #e0e0e0',
      },
    },
    entityName: {
      fontWeight: 600,
      fontSize: '14px',
      color: theme.color.grey.neutralBrand900,
    },
    cellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral400,
    },
    mutedCellText: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
    },
    typeRow: {
      display: 'flex',
      alignItems: 'center',
    },
    typeIcon: {
      fontSize: '16px',
      color: theme.color.grey.neutral200,
      marginRight: '6px',
    },
    replaced: {
      fontSize: '13px',
      color: theme.color.grey.neutral200,
      textDecoration: 'line-through',
    },
    applied: {
      fontSize: '13px',
      color: theme.color.grey.neutralBrand900,
    },
    statusChip: {
      height: '20px',
      fontSize: '11px',
    },
  })
);

const statusLabel = (entry: HistoryEntry) => {
  if (entry.status === 'rejected') return 'Rejected';
  if (entry.status === 'superseded') return 'Superseded by a later push';
  return entry.pushMode === 'force' ? 'Applied automatically' : 'Accepted';
};

export const AdminConsoleUpdateHistory = () => {
  const classes = useStyles();
  const { data, loading } = useCampfireQuery<GetUpdateHistory, {}>(GET_UPDATE_HISTORY);

  const entries = data?.vm.enterpriseUpdateHistory ?? [];

  return (
    <Box className={classes.container}>
      <Box className={classes.heading}>
        <Box>
          <Typography variant='h2' className={classes.headingTitle}>
            Update History
          </Typography>
          <Typography variant='body2'>{'Updates received from your master account'}</Typography>
        </Box>
      </Box>

      {loading && <LinearProgress style={{ marginTop: '1rem' }} />}

      {!loading && entries.length === 0 ? (
        <Box className={classes.containerPanel}>
          <Typography className={classes.headingPanel}>
            <History style={{ fontSize: '14px', paddingRight: '5px' }} /> No updates yet
          </Typography>
          <Typography variant='body2' className={classes.bodyPanel}>
            {'A record of every update from your master account, including changes that were applied automatically.'}
          </Typography>
        </Box>
      ) : (
        !loading && (
          <Box className={classes.containerPanel}>
            <Typography className={classes.headingPanel}>
              <History style={{ fontSize: '14px', paddingRight: '5px' }} /> Resolved updates
            </Typography>
            <Typography variant='body2' className={classes.bodyPanel}>
              {`${entries.length} update${entries.length === 1 ? '' : 's'} from your master account.`}
            </Typography>

            <TableContainer style={{ marginTop: '1rem' }}>
              <Table>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell>Entity</TableCell>
                    <TableCell>Change</TableCell>
                    <TableCell>Detail</TableCell>
                    <TableCell>Outcome</TableCell>
                    <TableCell align='right'>Resolved</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((entry) => {
                    const TypeIcon = entityTypeIcon(entry.rootEntityType);
                    const accepted = entry.status === 'accepted';

                    return (
                      <TableRow key={entry.diffId} hover>
                        <TableCell>
                          <Typography className={classes.entityName}>{entry.rootName}</Typography>
                          <Box className={classes.typeRow}>
                            <TypeIcon className={classes.typeIcon} />
                            <Typography className={classes.mutedCellText}>
                              {entityTypeHeading(entry.rootEntityType)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography className={classes.cellText}>{entry.summary}</Typography>
                          {entry.itemName !== '' && (
                            <Typography className={classes.mutedCellText}>{entry.itemName}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.replacedValue !== '' && (
                            <Typography className={classes.replaced}>{entry.replacedValue}</Typography>
                          )}
                          {entry.appliedValue !== '' && (
                            <Typography className={classes.applied}>{entry.appliedValue}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            variant={accepted ? 'default' : 'outlined'}
                            color={accepted ? 'primary' : 'default'}
                            label={statusLabel(entry)}
                            className={classes.statusChip}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <Typography className={classes.mutedCellText}>
                            {entry.dateResolved ? unpackToDateTime(entry.dateResolved).toFormat('d MMM y') : ''}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )
      )}
    </Box>
  );
};
